import { useState } from "react";
import type { ReactNode } from "react";

import { Modal } from "./Modal";

// Se re-exporta para que una página importe el modal del mismo sitio que el
// resto de las piezas, en lugar de tener dos imports de `components/`.
export { Modal };

// Piezas compartidas con los cuatro estados que §6 exige en toda vista de
// datos: loading, empty, error y success.

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`panel ${className}`}>{children}</div>;
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="bg-gradient-to-br from-white to-slate-400 bg-clip-text text-xl font-semibold tracking-tight text-transparent">
          {title}
        </h1>
        {description && <p className="mt-1.5 max-w-prose text-sm text-slate-400">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "danger" }) {
  const styles = {
    // El primario es el único con degradado y resplandor: en una pantalla
    // oscura, dos botones que brillan dejan de señalar cuál es la acción.
    primary:
      "bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow-[0_0_20px_-6px_rgba(56,189,248,0.7)] " +
      "hover:shadow-[0_0_28px_-4px_rgba(56,189,248,0.9)] disabled:from-slate-700 disabled:to-slate-700 " +
      "disabled:text-slate-400 disabled:shadow-none",
    secondary: "border border-white/15 bg-white/[0.04] text-slate-300 hover:bg-white/[0.09] hover:text-white",
    danger: "border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20",
  }[variant];

  // `className` se saca de `props` y se concatena. Antes entraba por el
  // spread y el literal de abajo lo pisaba en silencio: el `w-full` del login
  // nunca se aplicó, y cualquier otro ajuste puntual tampoco lo habría hecho.
  return (
    <button
      {...props}
      className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-sky-400/60 focus:ring-offset-2 focus:ring-offset-[#05070f] disabled:cursor-not-allowed ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && !error && <span className="mt-1.5 block text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1.5 block text-xs text-rose-300">{error}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3.5 py-2 text-sm text-slate-100 " +
  "placeholder:text-slate-600 transition focus:border-sky-400/60 focus:bg-white/[0.07] " +
  "focus:outline-none focus:ring-1 focus:ring-sky-400/60 " +
  // Los desplegables heredan el fondo del sistema en sus opciones, que en
  // oscuro sale blanco sobre blanco en algunos navegadores.
  "[&>option]:bg-[#11162a] [&>option]:text-slate-100";

export function Loading({ label = "Cargando…" }: { label?: string }) {
  return (
    <Card className="p-10 text-center">
      <span role="status" className="inline-flex items-center gap-2.5 text-sm text-slate-400">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sky-400/30 border-t-sky-400" />
        {label}
      </span>
    </Card>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <Card className="p-8 text-center">
      <p className="text-sm font-medium text-slate-200">{title}</p>
      {description && <p className="mx-auto mt-1.5 max-w-md text-sm text-slate-400">{description}</p>}
    </Card>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card className="border-rose-500/30 bg-rose-500/[0.07] p-6">
      <p className="text-sm font-medium text-rose-200">No se pudo cargar</p>
      <p className="mt-1 text-sm text-rose-300/90">{message}</p>
      {onRetry && (
        <div className="mt-4">
          <Button variant="secondary" onClick={onRetry}>
            Reintentar
          </Button>
        </div>
      )}
    </Card>
  );
}

// `className` se **añade** al del componente, no lo sustituye: una insignia
// dentro de una celda necesita a veces un margen, y sin esto hay que envolverla
// en un div sólo para eso.
export function Badge({
  children,
  tone = "neutral",
  className = "",
}: {
  children: ReactNode;
  tone?: "neutral" | "good" | "warn" | "bad";
  className?: string;
}) {
  const styles = {
    neutral: "border-white/10 bg-white/[0.06] text-slate-300",
    good: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    bad: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  }[tone];
  return (
    <span
      className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${styles} ${className}`}
    >
      {children}
    </span>
  );
}

/** Aviso de error de una acción (no de una carga), al principio de la vista. */
export function ActionError({ message }: { message: string }) {
  return (
    <Card className="mb-4 border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{message}</Card>
  );
}

/**
 * Confirm pide una confirmación en un diálogo.
 *
 * No usa `window.confirm` a propósito: un diálogo nativo no se puede explicar
 * ni mostrar las consecuencias, y en una acción destructiva lo que hace falta
 * es justamente eso. Cuando se pasa `requireText`, hay que teclearlo para que
 * el botón se habilite.
 *
 * Va en un modal y no como aviso encima de la tabla, que es como estaba antes:
 * en una lista larga el aviso aparecía fuera de la vista y el usuario pulsaba
 * «Eliminar» sin ver que le estaban preguntando algo.
 */
export function Confirm({
  title,
  description,
  confirmLabel,
  requireText,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  requireText?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const unlocked = requireText === undefined || typed === requireText;

  return (
    // Mientras la acción corre, cerrar queda bloqueado: el Esc o el clic en el
    // fondo dejarían al usuario sin saber si se ejecutó.
    <Modal title={title} onClose={busy ? () => {} : onCancel}>
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.07] p-3 text-sm text-rose-200">
        {description}
      </div>

      {requireText !== undefined && (
        <div className="mt-4 max-w-sm">
          <Field label={`Escribe ${requireText} para confirmar`}>
            <input
              className={inputClass}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </Field>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="danger" disabled={!unlocked || busy} onClick={onConfirm}>
          {busy ? "Ejecutando…" : confirmLabel}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}
